// src/utils/ShareModal.js - Mobile-friendly sharing modal, consistent with
// the rest of the app's bottom-sheet modal style (see EventDetail.js's mini
// profile modal for the same flex items-end/sm:items-center pattern).
import React, { useState, useEffect } from 'react';
import {
  FaWhatsapp,
  FaFacebook,
  FaWeixin,  // WeChat icon
  FaQrcode,
  FaCopy,
  FaShare
} from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import {
  shareViaWebShare,
  copyEventToClipboard,
  copyEventToClipboardLang,
  generateEventQRCode,
  downloadQRCode,
  getAvailableShareMethods,
  getEventUrl
} from '../utils/shareUtils';

function ShareModal({ event, venue, registrationCount, isOpen, onClose }) {
  const { t } = useLanguage();
  const [qrCode, setQrCode] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showWeChatGuide, setShowWeChatGuide] = useState(false);
  const [copying, setCopying] = useState(false);
  const [showCopyLanguage, setShowCopyLanguage] = useState(false);

  useEffect(() => {
    if (isOpen && !qrCode) {
      generateQR();
    }
    // Reset to the main share-options view each time the modal reopens,
    // rather than reopening on whatever sub-view was last shown.
    if (isOpen) {
      setShowQRCode(false);
      setShowWeChatGuide(false);
      setShowCopyLanguage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const generateQR = async () => {
    setLoadingQR(true);
    try {
      const qrCodeData = await generateEventQRCode(event.id);
      setQrCode(qrCodeData);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setLoadingQR(false);
    }
  };

  const handleShare = async (method) => {
    try {
      switch (method) {
        case 'native':
          await shareViaWebShare(event, venue, registrationCount);
          break;

        case 'copy':
          setShowCopyLanguage(true);
          break;

        case 'qr':
          setShowQRCode(true);
          break;

        case 'wechat':
          setShowWeChatGuide(true);
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('Error sharing:', error);
      showNotification(t('share.errorSharing'), 'error');
    }
  };

  const handleCopyWithLanguage = async (lang) => {
    try {
      setCopying(true);
      await copyEventToClipboardLang(event, venue, registrationCount, lang);
      showNotification(t('share.copiedToClipboard'), 'success');
      setTimeout(() => { setCopying(false); setShowCopyLanguage(false); }, 800);
    } catch (error) {
      setCopying(false);
      showNotification(t('share.errorCopying'), 'error');
    }
  };

  const handleCopyForWeChat = async () => {
    try {
      setCopying(true);
      await copyEventToClipboard(event, venue, registrationCount);
      showNotification(t('share.copiedForWechat'), 'success');
      setTimeout(() => setCopying(false), 1000);
    } catch (error) {
      setCopying(false);
      showNotification(t('share.errorCopying'), 'error');
    }
  };

  const handleDownloadQR = async () => {
    try {
      await downloadQRCode(event.id, `${event.title.replace(/\s+/g, '-').toLowerCase()}-qr.png`);
      showNotification(t('share.qrDownloaded'), 'success');
    } catch (error) {
      showNotification(t('share.errorDownloadingQr'), 'error');
    }
  };

  const showNotification = (message, type) => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[10000] p-3 rounded-xl border shadow-lg ${
      type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
      type === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-blue-50 border-blue-200 text-blue-800'
    }`;
    notification.innerHTML = `
      <div class="flex items-center text-sm">
        <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          ${type === 'success' ?
            '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>' :
            type === 'error' ?
            '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>' :
            '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>'
          }
        </svg>
        <span>${message}</span>
      </div>
    `;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 3000);
  };

  const getShareIcon = (iconType) => {
    const iconMap = {
      share: <FaShare className="w-5 h-5" />,
      copy: <FaCopy className="w-5 h-5" />,
      qr: <FaQrcode className="w-5 h-5" />,
      whatsapp: <FaWhatsapp className="w-5 h-5" />,
      facebook: <FaFacebook className="w-5 h-5" />,
      wechat: <FaWeixin className="w-5 h-5" />
    };

    return iconMap[iconType] || iconMap.share;
  };

  const getMethodLabel = (method) => {
    const labelMap = {
      native: { name: t('share.methodNative'), desc: t('share.methodNativeDesc') },
      copy: { name: t('share.methodCopy'), desc: t('share.methodCopyDesc') },
      qr: { name: t('share.methodQr'), desc: t('share.methodQrDesc') },
      wechat: { name: t('share.methodWechat'), desc: t('share.methodWechatDesc') },
    };
    return labelMap[method.id] || { name: method.name, desc: method.description };
  };

  if (!isOpen) return null;

  // Capacity: spots left / full, same convention as the event card on Home
  // (Math.max(0, max - current), green "N spots left" vs orange "Full").
  const maxParticipants = event?.maxParticipants || 0;
  const currentCount = registrationCount ?? event?.currentParticipants ?? 0;
  const spotsLeft = Math.max(0, maxParticipants - currentCount);
  const isFull = maxParticipants > 0 && spotsLeft === 0;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[92vh] sm:max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">{t('share.title')}</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Event Preview */}
          <div className="mb-5 p-4 bg-orange-50 rounded-2xl border border-orange-100">
            <h4 className="text-sm font-bold text-gray-900 mb-2.5 truncate">{event.title}</h4>
            <div className="text-xs text-gray-600 space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-base flex-shrink-0">📅</span>
                <span className="truncate">{new Date(event.startDate).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })}</span>
                <span className="text-gray-400">·</span>
                <span className="truncate">{new Date(event.startDate).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-base flex-shrink-0">📍</span>
                <span className="truncate">{venue ? venue.name : event.venueName}</span>
              </div>
              {event.price > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-base flex-shrink-0">💰</span>
                  <span>${event.price}</span>
                </div>
              )}
              {/* Capacity — spots left / full, same wording as the event card on Home */}
              {maxParticipants > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-base flex-shrink-0">👥</span>
                  <span>{t('share.capacity', { current: currentCount, max: maxParticipants })}</span>
                  <span className="text-gray-400">·</span>
                  {isFull ? (
                    <span className="font-semibold text-orange-500">{t('share.full')}</span>
                  ) : (
                    <span className="font-semibold text-green-600">{t('share.spotsLeft', { count: spotsLeft })}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Share Options */}
          {!showQRCode && !showWeChatGuide && !showCopyLanguage && (
            <div>
              <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2.5">{t('share.chooseMethod')}</h5>
              <div className="space-y-2">
                {getAvailableShareMethods().map((method) => {
                  const label = getMethodLabel(method);
                  return (
                    <button
                      key={method.id}
                      onClick={() => handleShare(method.id)}
                      disabled={copying && method.id === 'copy'}
                      className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                          method.id === 'wechat' ? 'bg-green-100 text-green-600' :
                          method.id === 'qr' ? 'bg-gray-100 text-gray-600' :
                          method.id === 'copy' ? 'bg-gray-100 text-gray-600' :
                          'bg-orange-100 text-orange-600'
                        }`}>
                          {getShareIcon(method.icon)}
                        </div>
                        <div className="text-left min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{label.name}</div>
                          <div className="text-[11px] text-gray-400 truncate">{label.desc}</div>
                        </div>
                      </div>
                      {copying && method.id === 'copy' ? (
                        <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                      ) : (
                        <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Copy Language Picker */}
          {showCopyLanguage && (
            <div>
              <button
                onClick={() => setShowCopyLanguage(false)}
                className="mb-4 text-orange-600 hover:text-orange-700 flex items-center text-sm font-medium transition-colors"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('share.back')}
              </button>

              <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <FaCopy className="w-4 h-4 text-gray-500" />
                {t('share.methodCopy')}
              </h3>
              <p className="text-xs text-gray-500 mb-4">{t('share.copyLanguageHint')}</p>

              <div className="space-y-2">
                <button
                  onClick={() => handleCopyWithLanguage('en')}
                  disabled={copying}
                  className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🇬🇧</span>
                    <div className="text-left">
                      <div className="text-sm font-semibold text-gray-900">English</div>
                      <div className="text-[11px] text-gray-400">Copy event details in English</div>
                    </div>
                  </div>
                  {copying ? (
                    <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaCopy className="w-4 h-4 text-gray-300" />
                  )}
                </button>

                <button
                  onClick={() => handleCopyWithLanguage('zh')}
                  disabled={copying}
                  className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🇨🇳</span>
                    <div className="text-left">
                      <div className="text-sm font-semibold text-gray-900">中文</div>
                      <div className="text-[11px] text-gray-400">复制中文版活动详情</div>
                    </div>
                  </div>
                  {copying ? (
                    <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaCopy className="w-4 h-4 text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* QR Code View */}
          {showQRCode && (
            <div className="text-center">
              <button
                onClick={() => setShowQRCode(false)}
                className="mb-4 text-orange-600 hover:text-orange-700 flex items-center text-sm font-medium transition-colors"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('share.back')}
              </button>

              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center justify-center gap-2">
                <FaQrcode className="w-4 h-4 text-gray-500" />
                {t('share.qrHeading')}
              </h3>

              {loadingQR ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-gray-500">{t('share.qrGenerating')}</span>
                </div>
              ) : qrCode ? (
                <div className="space-y-4">
                  <div className="inline-block p-4 bg-white border-2 border-gray-100 rounded-2xl shadow-sm">
                    <img src={qrCode} alt="Event QR Code" className="w-48 h-48 mx-auto" />
                  </div>
                  <p className="text-xs text-gray-500">{t('share.qrHint')}</p>
                  <button
                    onClick={handleDownloadQR}
                    className="w-full sm:w-auto px-5 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 active:bg-orange-700 transition-colors flex items-center justify-center gap-2 text-sm font-semibold mx-auto"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('share.qrDownload')}
                  </button>
                </div>
              ) : (
                <div className="py-10">
                  <div className="text-red-600 mb-3 text-sm">{t('share.qrFailed')}</div>
                  <button
                    onClick={generateQR}
                    className="px-4 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors text-sm font-medium"
                  >
                    {t('share.tryAgain')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* WeChat Share Guide */}
          {showWeChatGuide && (
            <div className="text-center">
              <button
                onClick={() => setShowWeChatGuide(false)}
                className="mb-4 text-orange-600 hover:text-orange-700 flex items-center text-sm font-medium transition-colors"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('share.back')}
              </button>

              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <FaWeixin className="w-6 h-6 text-green-600" />
                </div>
              </div>

              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('share.wechatHeading')}</h3>

              {/* Pointer to the ··· menu, which lives at the top-right of WeChat's own bar.
                  Always shows both EN and ZH here regardless of the app's selected
                  language — this guide is specifically for the WeChat in-app browser,
                  whose own UI is typically in Chinese, so both are kept visible. */}
              <div className="relative bg-green-50 border border-green-200 rounded-2xl p-4 mb-4">
                <div className="absolute -top-2.5 right-4 text-lg animate-bounce">☝️</div>
                <p className="text-xs text-gray-800 font-medium leading-relaxed">
                  {t('share.wechatInstructionEn')}
                </p>
                <p className="text-[11px] text-gray-600 mt-1.5" lang="zh">
                  {t('share.wechatInstructionZh')}
                </p>
              </div>

              <p className="text-[11px] text-gray-500 mb-2">{t('share.orCopyHint')}</p>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-3 text-left">
                <p className="text-[10px] text-gray-500 mb-0.5">{t('share.eventLink')}</p>
                <p className="text-xs text-orange-600 break-all">{getEventUrl(event.id)}</p>
              </div>

              <button
                onClick={handleCopyForWeChat}
                disabled={copying}
                className="w-full px-5 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 active:bg-green-800 transition-colors flex items-center justify-center gap-2 text-sm font-semibold disabled:opacity-50"
              >
                {copying ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FaCopy className="w-4 h-4" />
                )}
                {t('share.copyDetailsAndLink')}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {!showQRCode && !showWeChatGuide && (
          <div className="flex-shrink-0 px-5 py-3 border-t border-gray-100 bg-gray-50">
            <p className="text-[11px] text-gray-500 text-center">{t('share.footerTagline')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ShareModal;